import {changeFavicon, getPublicLoginPath, isSelectiveAiDomains} from 'common/HelperFunctions'
import ConnectivityBanner from 'common/components/ConnectivityBanner'
import Spinner from 'common/components/Spinner'
import GlobalChatNotificationListener from 'common/components/chat/GlobalChatNotificationListener'
import {M_LAYOUT} from 'common/constants'
import {handlePostLoginNavigation} from 'common/helpers/authRedirect'
import {
  DEFAULT_TOAST_AUTO_CLOSE_MS,
  installErrorToastDuration,
} from 'common/helpers/errorToastDuration'
import {installNativePickerAutoOpen} from 'common/helpers/nativePickerAutoOpen'
import {startRecording, stopRecording} from 'common/sessionRecording/sessionRecorder'
import {FC, Suspense, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {Outlet, useLocation, useNavigate, useSearchParams} from 'react-router'
import {Slide, ToastContainer, toast} from 'react-toastify'
import TopBarProgress from 'react-topbar-progress-indicator'
import {RootState} from 'setup'
import {setConnectivityStatus} from 'setup/redux/Actions/Connectivity/connectivity'
import {REDUX_SET_ACCESS_TOKEN} from 'setup/redux/Actions/SignIn/ActionTypes/ReduxActionTypes'
import {loginWithJWT} from 'setup/redux/Actions/SignIn/login'
import {getAccessToken} from 'setup/redux/Actions/Timetable/timetable'
import {REDUX_SET_URDD_ID} from 'setup/redux/Actions/User/ActionTypes/ReduxActionTypes'
import socket from 'socket'

import {toAbsoluteUrl} from '_metronic/helpers'
import {isRtl} from '_metronic/i18n/Metronici18n'
import {MasterInit} from '_metronic/layout/MasterInit'
import {SupportStaffAlert} from '_metronic/layout/components/header/SupportStaffAlert'
import {LayoutProvider, LayoutSplashScreen} from '_metronic/layout/core'
import {ThemeModeProvider, getTheme} from '_metronic/partials'

import {
  clearMustCreatePassword,
  clearMustCreatePasswordStorage,
  loginResultRequiresPasswordChange,
  resolveSelectiveAiHandoffDestination,
  setMustCreatePassword,
} from './modules/auth/selective/mustCreatePasswordHelpers'
import ChatbotHost from './pages/chatbot/ChatbotHost'
import {ChatbotModalProvider} from './pages/chatbot/hooks/ChatbotModalContext'
import {shouldMountChatbot} from './pages/chatbot/modes/assistant/chatSurface'
import {useChatViewer} from './pages/chatbot/modes/assistant/useChatViewer'
import {PageContextProvider} from './pages/chatbot/pageContext/PageContextStore'
import ChatbotWalkthroughRunner from './pages/chatbot/walkthrough/core/ChatbotWalkthroughRunner'
import DynamicWalkthroughProvider from './pages/chatbot/walkthrough/core/DynamicWalkthroughProvider'

// Module scope, before any toast can fire: error toasts get a read-length duration app-wide.
installErrorToastDuration()
// Clicking anywhere on a native date/time input opens its calendar/clock picker, app-wide.
installNativePickerAutoOpen()

const VERSION_UPDATE_TOAST_ID = 'react-portal-version-update'

/** Best-effort ordering for deploy tags (semver-like vs lexical). Returns negative if a < b. */
function compareDeployVersions(a: string, b: string): number {
  const strip = (s: string) => s.trim().replace(/^v/i, '')
  const na = strip(a)
  const nb = strip(b)
  const semverPrefix = /^(\d+)\.(\d+)\.(\d+)/
  const ma = na.match(semverPrefix)
  const mb = nb.match(semverPrefix)
  if (ma && mb) {
    for (let i = 1; i <= 3; i++) {
      const d = parseInt(ma[i], 10) - parseInt(mb[i], 10)
      if (d !== 0) return d
    }
  }
  return na.localeCompare(nb, undefined, {numeric: true, sensitivity: 'base'})
}

/** Highest server version we've surfaced in this tab (avoids older/out-of-order socket events). */
let latestHandledServerVersion: string | null = null

const UpdateToast: FC<{closeToast?: () => void; newVersion: string}> = ({
  closeToast,
  newVersion,
}) => {
  const intl = useIntl()
  return (
    <div className='d-flex align-items-center justify-content-between' style={{gap: '8px'}}>
      <span className='fw-bold'>{intl.$t({id: 'GENERAL.NEW_VERSION_AVAILABLE'})}</span>
      <div className='d-flex gap-2'>
        <button
          className='btn btn-light-secondary btn-sm btn-icon'
          onClick={() => {
            closeToast?.()
            localStorage.setItem(
              'version-update-dismissed',
              JSON.stringify({
                timestamp: new Date().toISOString(),
                buildVersion: import.meta.env.VITE_BUILD_VERSION,
                environment: import.meta.env.VITE_APP_BUILD_NAME,
                newVersion,
              })
            )
          }}
        >
          <i className='fa fa-times' />
        </button>
        <button
          className='btn btn-light-primary btn-sm btn-icon'
          onClick={() => {
            if ('caches' in window) {
              caches.keys().then((keys) => keys.forEach((key) => caches.delete(key)))
            }
            window.location.reload()
          }}
        >
          <i className='fa fa-sync-alt' />
        </button>
      </div>
    </div>
  )
}

function showUpdateToast(newVersion: string) {
  // Prevent stacking when multiple socket events arrive quickly / out-of-order
  toast.dismiss(VERSION_UPDATE_TOAST_ID)

  toast(
    <UpdateToast
      newVersion={newVersion}
      closeToast={() => toast.dismiss(VERSION_UPDATE_TOAST_ID)}
    />,
    {
      toastId: VERSION_UPDATE_TOAST_ID,
      position: 'top-center',
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    }
  )
}

function onNewVersion(data: any) {
  console.log('react-portal-build-version', data)
  if (
    String(data.environment) !== String(import.meta.env.VITE_APP_BUILD_NAME) ||
    String(data.buildVersion) === String(import.meta.env.VITE_BUILD_VERSION)
  ) {
    return
  }

  const incoming = String(data.buildVersion)

  const dismissed = localStorage.getItem('version-update-dismissed')
  if (dismissed) {
    try {
      const dismissedData = JSON.parse(dismissed)
      if (dismissedData.newVersion === incoming) return
    } catch {
      /* ignore malformed */
    }
  }

  if (
    latestHandledServerVersion !== null &&
    compareDeployVersions(incoming, latestHandledServerVersion) < 0
  ) {
    return
  }

  if (incoming === latestHandledServerVersion && toast.isActive(VERSION_UPDATE_TOAST_ID)) {
    return
  }

  latestHandledServerVersion = incoming
  showUpdateToast(incoming)
}

const App: FC = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [loggingIn, setLoggingIn] = useState(false)
  const jwtHandoffStarted = useRef(false)
  const rtl = isRtl()
  const theme = getTheme()

  if (searchParams.get('mlayout') === 'false') {
    sessionStorage.removeItem(M_LAYOUT)
  } else if (searchParams.get('mlayout') === 'true') {
    sessionStorage.setItem(M_LAYOUT, 'true')
  }

  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings
  ) as any
  const generalSettingsLoaded = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettingsLoaded
  ) as boolean

  const isLoggedIn = useSelector<RootState>(({userReducer}) => userReducer.isLoggedIn) as boolean
  const chatViewer = useChatViewer()
  const accessToken = useSelector<RootState>(({userReducer}) => userReducer.accessToken) as
    | string
    | null
  const urddId = useSelector<RootState>(({userReducer}) => userReducer.urddId) as string | null
  const mustCreatePassword = useSelector<RootState>(
    ({userReducer}) => (userReducer as any).mustCreatePassword
  ) as boolean

  if (isSelectiveAiDomains()) {
    changeFavicon(
      `${toAbsoluteUrl('media/logos/Da1ogo.png')}?v=${import.meta.env.VITE_BUILD_VERSION}`
    )
  } else if (generalSettings?.favicon) {
    changeFavicon(generalSettings.favicon)
  } else if (generalSettingsLoaded) {
    // Only once the server has answered and this tenant has no favicon of its own.
    // Until then the tab stays blank (index.html) instead of flashing the default.
    changeFavicon(toAbsoluteUrl('favicon.ico'))
  }

  TopBarProgress.config({
    barColors: {
      '0': '#1b84ff',
    },
    barThickness: 2,
    shadowBlur: 5,
  })

  useEffect(() => {
    // Hide the HTML splash screen once React has mounted.
    // Downstream components handle their own loading states from here on.
    if (isSelectiveAiDomains()) {
      document.body.classList.add('app-ready')
    }
  }, [])

  useEffect(() => {
    const handleOnline = () => dispatch(setConnectivityStatus(true))
    const handleOffline = () => dispatch(setConnectivityStatus(false))
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    // Don't dispatch navigator.onLine on mount — it's unreliable during refresh.
    // The online/offline events are the reliable source of truth.
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [dispatch])

  // Session recording follows the token, which covers both entry points in one
  // place: a fresh login, and app boot with a session already persisted. Logout
  // clears the token, so the recording stops and the next login starts a new
  // one — a recording belongs to exactly one user.
  useEffect(() => {
    if (accessToken) {
      startRecording()
    } else {
      stopRecording()
    }
  }, [accessToken])

  useEffect(() => {
    if (!isSelectiveAiDomains() || !isLoggedIn) return
    if (mustCreatePassword) {
      localStorage.setItem('must_create_password', 'true')
    } else {
      clearMustCreatePasswordStorage()
    }
  }, [isLoggedIn, mustCreatePassword])

  // Fetch URDD id once (for tenants that support it) and store in Redux for downstream features
  // like timetable export logging.
  useEffect(() => {
    if (!isLoggedIn) return
    if (urddId) return

    dispatch(
      getAccessToken(
        (response: any) => {
          const {urdd_id} = response?.result || response || {}
          if (urdd_id !== undefined && urdd_id !== null && String(urdd_id).trim() !== '') {
            dispatch({type: REDUX_SET_URDD_ID, payload: urdd_id})
          }
        },
        () => {
          // best-effort only; no UI impact
        }
      )
    )
  }, [dispatch, isLoggedIn, urddId])

  useEffect(() => {
    let search = searchParams.toString()
    if (search) {
      search = `?${search}`
    }

    // One-time universal-login handoff is handled by Auth.tsx on /auth?auth_token=...
    const authToken = searchParams.get('rauth_token')
    if (authToken && accessToken === authToken && !mustCreatePassword) {
      // Same token as the current session: re-exchanging it would invalidate
      // that session, so just clean the URL. A *different* rauth_token is a new
      // handoff (e.g. mobile WebView after a user switch) and must be exchanged
      // even though an old session is still persisted on this origin.
      const destination = resolveSelectiveAiHandoffDestination(location.pathname)
      const nextSearch = new URLSearchParams(searchParams)
      nextSearch.delete('rauth_token')
      if (destination.pathname === '/dashboard' && !nextSearch.has('step')) {
        nextSearch.set('step', '0')
      }
      const cleanedSearch = nextSearch.toString()
      navigate(
        {
          pathname: destination.pathname,
          search: cleanedSearch ? `?${cleanedSearch}` : destination.search,
        },
        {replace: true}
      )
    } else if (authToken) {
      if (jwtHandoffStarted.current) return
      jwtHandoffStarted.current = true

      // The URL token is authoritative: drop the stale persisted session
      // synchronously before the exchange resolves, otherwise background
      // features (socket, chatbot) can fire an authenticated call with the old
      // token, 401, and trip the forced /logout redirect mid-exchange — and the
      // WebView keeps landing as the previously logged-in user.
      if (accessToken) {
        dispatch({type: REDUX_SET_ACCESS_TOKEN, payload: null})
      }

      setLoggingIn(true)

      const onSuccess = (res: any) => {
        setLoggingIn(false)

        const proceed = () => {
          if (
            handlePostLoginNavigation(res.result, {
              path: '/dashboard',
              searchParams: {step: '0'},
            })
          ) {
            return
          }

          const destination = resolveSelectiveAiHandoffDestination(location.pathname)
          const nextSearch = new URLSearchParams(searchParams)
          nextSearch.delete('rauth_token')
          if (destination.pathname === '/dashboard' && !nextSearch.has('step')) {
            nextSearch.set('step', '0')
          }
          const cleanedSearch = nextSearch.toString()

          setTimeout(() => {
            navigate(
              {
                pathname: destination.pathname,
                search: cleanedSearch ? `?${cleanedSearch}` : destination.search,
              },
              {replace: true}
            )
          }, 500)
        }

        if (loginResultRequiresPasswordChange(res?.result)) {
          setMustCreatePassword(dispatch)
          navigate('/create-password', {replace: true})
        } else {
          clearMustCreatePassword(dispatch)
          proceed()
        }
      }

      const onFailure = (err: any) => {
        jwtHandoffStarted.current = false
        // Selective AI domains fail silently; other domains keep the error toast.
        if (!isSelectiveAiDomains()) {
          const message =
            err?.response?.data?.message ??
            err?.response?.message ??
            err?.message ??
            'Invalid Auth Token'
          toast.error(message)
        }
        // Stop the full-screen spinner and send the user to the login screen,
        // dropping the stale/expired token from the URL. Without this the
        // `loggingIn` flag stays true and the app spins forever on a failed
        // (e.g. expired) JWT handoff instead of showing the login form.
        setLoggingIn(false)
        navigate(getPublicLoginPath(), {replace: true})
      }

      dispatch(loginWithJWT(authToken, onSuccess, onFailure))
    }

    socket.emit('react-portal-build-version', {environment: import.meta.env.VITE_APP_BUILD_NAME})
    socket.on('react-portal-build-version', onNewVersion)

    return () => {
      socket.off('react-portal-build-version', onNewVersion)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loggingIn) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center h-100'>
        <Spinner size='lg' color='primary' />
      </div>
    )
  }

  return (
    // App-wide so Header.tsx (inside the layout) can open the chatbot via
    // openChatbot() — previously the provider only wrapped the widget mount.
    <ChatbotModalProvider>
      {/* Selective-AI domains only: support/impersonation banner rendered here, above every layout
          (Master, Wizard/onboarding, Iframe) and every route (create-password included), so it
          shows on every screen of a support session. Other domains keep rendering it inside
          MasterLayout. Renders null outside a support session. */}
      {isSelectiveAiDomains() && <SupportStaffAlert />}
      {isSelectiveAiDomains() && <ConnectivityBanner />}
      {/* Success/info toasts close after 3s; error toasts get a longer, length-scaled
          duration via installErrorToastDuration() (see common/helpers/errorToastDuration). */}
      <ToastContainer
        position='top-right'
        autoClose={DEFAULT_TOAST_AUTO_CLOSE_MS}
        closeButton={true}
        transition={Slide}
        hideProgressBar={true}
        rtl={rtl}
        theme={theme}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {/* Pages publish what they show (ids only) and the chatbot reads it when a message is sent,
          so the provider wraps both the routes and ChatbotHost. */}
      <PageContextProvider>
        <Suspense fallback={<LayoutSplashScreen />}>
          <LayoutProvider>
            <ThemeModeProvider>
              <DynamicWalkthroughProvider>
                <Outlet />
                <MasterInit />
                <ChatbotWalkthroughRunner apiResponse={null} />
              </DynamicWalkthroughProvider>
            </ThemeModeProvider>
          </LayoutProvider>
        </Suspense>

        {isLoggedIn && <GlobalChatNotificationListener />}

        {/* {isLoggedIn && isAIDevEnv() && <ChatbotPage />} */}
        {/* ChatbotHost decides which bot(s) this user gets and renders null when neither.
            Mounted for signed-in users, and for everyone on the applicant /admissions pages;
            never on the legacy admission chatbot page. */}
        {shouldMountChatbot(location.pathname, chatViewer) && <ChatbotHost />}
      </PageContextProvider>
    </ChatbotModalProvider>
  )
}

export {App}
